import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Award, ShieldCheck, Repeat, Home, BookOpen, Gauge, ArrowLeft, PanelLeftClose, PanelLeftOpen, Flag, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, isPlaceholder } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useProgress, TIER_COPY, SHOW_READINESS } from "@/lib/hooks";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LanguagePicker, MachineTranslationNote } from "@/components/LanguagePicker";
import { t as tr, tierLevel } from "@/lib/i18n";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  ["/dashboard", "My path", Home],
  ["/learn", "Learn", BookOpen],
  ...(SHOW_READINESS ? [["/readiness", "Readiness", Gauge]] : []),
  ["/certificates", "Certificates", Award],
];

const useNavList = () => {
  const { user } = useAuth();
  const { data } = useProgress();
  const strat = data?.tiers.find((t) => t.tier === "strategist");
  const capstone = user.track !== "executive" && strat && ["capstone_pending", "certified"].includes(strat.status);
  return [...NAV, ...(capstone ? [["/capstone", "Capstone", Flag]] : []), ...(user.role === "admin" ? [["/admin", "Admin", ShieldCheck]] : [])].map(([to, label, Icon]) => [to, tr(label), Icon]);
};

const SideLink = ({ to, label, Icon, collapsed }) => {
  const link = (
    <NavLink to={to} data-testid={`nav-${to.slice(1)}`} aria-label={label}
      className={({ isActive }) => `group relative flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition-colors ${isActive ? "bg-brand-primary/10 text-brand-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"} ${collapsed ? "justify-center" : ""}`}>
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-brand-primary" />}
          <Icon className="h-5 w-5 shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
  if (!collapsed) return link;
  return <Tooltip><TooltipTrigger asChild>{link}</TooltipTrigger><TooltipContent side="right">{label}</TooltipContent></Tooltip>;
};

const LevelChip = ({ collapsed }) => {
  const { user } = useAuth();
  const { data } = useProgress();
  const navigate = useNavigate();
  if (user.track === "executive") return collapsed ? null : <div data-testid="header-tier-badge" className="rounded-xl border border-border bg-muted/50 px-3 py-2 text-xs font-semibold">Executive Briefing</div>;
  if (!data) return null;
  const t = TIER_COPY[data.current_tier];
  return (
    <button type="button" data-testid="header-tier-badge" onClick={() => { navigate("/dashboard"); setTimeout(() => document.getElementById("levels")?.scrollIntoView({ behavior: "smooth", block: "center" }), 350); }} title={tr("Change level")}
      className={`flex w-full items-center gap-2 rounded-xl border border-border bg-muted/50 px-3 py-2 text-left text-xs font-semibold transition-colors hover:border-brand-primary ${collapsed ? "justify-center" : ""}`}>
      <span className="font-heading text-base text-brand-primary">{t.mark}</span>
      {!collapsed && <><span className="flex-1">{tierLevel(t.label)}</span><ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" /></>}
    </button>
  );
};

const UserMenu = ({ collapsed, side = "right" }) => {
  const { user, logout, refreshMe } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const other = user.track === "executive" ? "strategist" : "executive";
  const switchTrack = async () => {
    try {
      const { data } = await api.patch("/auth/track", { track: other });
      await refreshMe();
      qc.invalidateQueries();
      toast.success(other === "executive" ? "Switched to the Executive Briefing" : `Switched to the full programme${data.credited_modules.length ? ` · ${data.credited_modules.join(", ")} lessons marked as read` : ""}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger" className={`flex items-center gap-3 rounded-xl text-left transition-colors hover:bg-muted ${collapsed === undefined ? "" : "w-full p-2"} ${collapsed ? "justify-center" : ""}`}>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-primary font-heading text-sm font-bold text-brand-primary-fg">{(user.name || user.email)[0].toUpperCase()}</span>
        {collapsed === false && <span translate="no" className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{user.name}</span><span className="block truncate text-xs text-muted-foreground">{user.email}</span></span>}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align="end" className="w-60">
        <DropdownMenuLabel translate="no" className="truncate">{user.name}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user.track && <DropdownMenuItem data-testid="menu-switch-track" onClick={switchTrack}><Repeat className="mr-2 h-4 w-4" />{other === "executive" ? "Switch to Executive Briefing" : "Upgrade to the full programme"}</DropdownMenuItem>}
        {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><ShieldCheck className="mr-2 h-4 w-4" />White-label admin</DropdownMenuItem>}
        <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const HomeBack = ({ compact }) => {
  const { pathname } = useLocation();
  if (pathname === "/") return null;
  return (
    <Link to="/" data-testid="header-back-home" aria-label="Back to home page" className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-border px-3 text-sm font-semibold text-muted-foreground transition-colors hover:border-brand-primary hover:text-brand-primary">
      <ArrowLeft className="h-4 w-4" />{!compact && <span>{tr("Home")}</span>}
    </Link>
  );
};

const Sidebar = ({ edition }) => {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cias_sidebar") === "1");
  const items = useNavList();
  useEffect(() => {
    localStorage.setItem("cias_sidebar", collapsed ? "1" : "0");
    document.documentElement.style.setProperty("--sidebar-w", collapsed ? "76px" : "248px");
  }, [collapsed]);
  useEffect(() => () => { document.documentElement.style.setProperty("--sidebar-w", "0px"); }, []);
  return (
    <TooltipProvider delayDuration={150}>
      <aside data-testid="app-sidebar" style={{ width: "var(--sidebar-w)" }} className="no-print fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-border bg-card/80 backdrop-blur-xl transition-[width] duration-200 lg:flex">
        <div className={`flex h-16 items-center gap-2 px-4 ${collapsed ? "justify-center" : "justify-between"}`}>
          {!collapsed && <Link to="/" data-testid="header-home-link" aria-label="Home page" className="min-w-0"><Logo edition={edition} className={edition?.logo_shape === "square" ? "h-8" : "h-7"} /></Link>}
          <button type="button" data-testid="sidebar-collapse-toggle" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? "Expand menu" : "Collapse menu"} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        </div>
        <div className="px-3"><LevelChip collapsed={collapsed} /></div>
        <nav className="mt-4 flex flex-1 flex-col gap-1 px-3">
          {items.map(([to, label, Icon]) => <SideLink key={to} to={to} label={label} Icon={Icon} collapsed={collapsed} />)}
        </nav>
        <div className="space-y-2 border-t border-border p-3">
          <div className={`flex items-center gap-2 ${collapsed ? "flex-col" : ""}`}>
            <Link to="/" data-testid="sidebar-home-link" aria-label="Back to home page" className={`flex h-9 items-center gap-2 rounded-lg px-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${collapsed ? "" : "flex-1"}`}><ArrowLeft className="h-4 w-4" />{!collapsed && tr("Home page")}</Link>
            <ThemeToggle />
          </div>
          <LanguagePicker compact={collapsed} />
          {!collapsed && <MachineTranslationNote />}
          <UserMenu collapsed={collapsed} />
        </div>
      </aside>
    </TooltipProvider>
  );
};

const MobileTopBar = ({ edition }) => (
  <header className="no-print sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl lg:hidden">
    <div className="flex h-14 items-center justify-between gap-3 px-4">
      <div className="flex min-w-0 items-center gap-2">
        <HomeBack compact />
        <Link to="/" data-testid="mobile-home-link" aria-label="Home page" className="min-w-0"><Logo edition={edition} className={edition?.logo_shape === "square" ? "h-7" : "h-6"} /></Link>
      </div>
      <div className="flex items-center gap-2"><LanguagePicker compact /><ThemeToggle /><UserMenu side="bottom" /></div>
    </div>
  </header>
);

const BottomNav = () => {
  const items = useNavList().slice(0, 5);
  return (
    <nav data-testid="mobile-bottom-nav" className="no-print fixed bottom-0 left-0 z-50 flex h-16 w-full items-stretch justify-around border-t border-border bg-background/95 backdrop-blur-xl lg:hidden">
      {items.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} data-testid={`mobile-nav-${to.slice(1)}`} className={({ isActive }) => `flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors ${isActive ? "text-brand-primary" : "text-muted-foreground"}`}>
          {({ isActive }) => <><span className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${isActive ? "bg-brand-primary/10" : ""}`}><Icon className="h-5 w-5" /></span>{label}</>}
        </NavLink>
      ))}
    </nav>
  );
};

const PublicHeader = ({ edition }) => (
  <>
  <header className="no-print sticky top-0 z-40 border-b border-border bg-background/90 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-8">
      <div className="flex shrink-0 items-center gap-3">
        <HomeBack />
        <Link to="/" data-testid="header-home-link" aria-label="Home page" className="shrink-0"><Logo edition={edition} className={edition?.logo_shape === "square" ? "h-8" : "h-7"} /></Link>
      </div>
      <div className="flex items-center gap-2">
        <LanguagePicker compact />
        <ThemeToggle />
        <Link to="/login" data-testid="header-signin-link" className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-fg transition-opacity hover:opacity-90">Sign in</Link>
      </div>
    </div>
  </header>
  <MachineTranslationNote bar />
  </>
);

export const Header = () => {
  const { user } = useAuth();
  const { edition } = useBrand();
  if (!user) return <PublicHeader edition={edition} />;
  return <><Sidebar edition={edition} /><MobileTopBar edition={edition} /><BottomNav /></>;
};

export const Footer = () => {
  const { edition } = useBrand();
  if (!edition) return null;
  return (
    <footer className="no-print mt-24 border-t border-border pb-16 lg:pb-0">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-10 text-sm text-muted-foreground sm:px-8 md:flex-row md:items-center md:justify-between">
        <div>
          <span className="font-semibold text-foreground">{edition.edition_label}</span> · Edition v{edition.edition_version} · Core content v{edition.content_version}
        </div>
        <div className="flex flex-wrap gap-5">
          {!isPlaceholder(edition.legal_url) && <a data-testid="footer-legal-link" href={edition.legal_url} target="_blank" rel="noreferrer" className="hover:text-foreground">Legal</a>}
          {!isPlaceholder(edition.privacy_url) && <a data-testid="footer-privacy-link" href={edition.privacy_url} target="_blank" rel="noreferrer" className="hover:text-foreground">Privacy</a>}
          {!isPlaceholder(edition.support_email) && <a data-testid="footer-support-link" href={`mailto:${edition.support_email}`} className="hover:text-foreground">Support</a>}
          <Link data-testid="footer-verify-link" to="/verify" className="hover:text-foreground">Verify a credential</Link>
        </div>
      </div>
    </footer>
  );
};

export const PageShell = ({ children, wide = false }) => (
  <main className={`mx-auto ${wide ? "max-w-6xl" : "max-w-4xl"} px-4 pb-28 pt-8 sm:px-8 md:pt-12 lg:pb-14 fade-up`}>{children}</main>
);
